import { documentsForPlacement, SALES_NAV_SECTIONS, STATIC_SALES_SCREENS, sortSalesDocuments } from "../constants/navigationCatalogue";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { usePermissions } from "../context/PermissionContext";
import { useTopNavMenu } from "../context/TopNavMenuContext";
import { DocumentType } from "../types/Types";
import { getDocumentPath } from "../routes/documentPath";
import TopNavLink from "./TopNavLink";

type Entry = {
  key: string;
  path: string;
  label: string;
  state?: Record<string, unknown>;
  /** draws a separator line above this entry (used for the control-panel link) */
  divider?: boolean;
};

const docState = (doc: DocumentType) => ({
  _id: doc._id,
  code: doc.code,
  name: doc.name,
  category: doc.category,
  subCategory: doc.subCategory,
  subType: doc.subType,
});

const TopNonMaterialMenu: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate } = usePermissions();
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const { activeMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose } = useTopNavMenu();
  const open = activeMenu === "nonMaterial";

  const salesDocs = useMemo(() => {
    return sortSalesDocuments(documentsForPlacement(documentTypes, "sales"));
  }, [documentTypes]);

  if (!hasAccess("NON_MATERIAL_DOCUMENTS")) return null;

  const documentEntries: Entry[] = salesDocs
    .filter((doc) => hasDocNavigate(doc.code))
    .map((doc) => ({
      key: doc._id,
      path: getDocumentPath(doc),
      label: `${doc.code} ${doc.name}`.trim(),
      state: docState(doc),
    }));

  const existingSalesCodes = new Set(salesDocs.map((doc) => doc.code));
  const screenEntries: Entry[] = STATIC_SALES_SCREENS
    .filter((screen) => !existingSalesCodes.has(screen.code))
    .map((screen) => {
      const label = t(screen.labelKey, screen.fallback);
      return {
        key: screen.code,
        path: `/non-material/sales/${screen.code}`,
        label: `${screen.code} ${label}`,
        state: {
          _id: screen.code,
          code: screen.code,
          name: label,
          category: "non-material",
          subCategory: "sales",
        },
      };
    });

  const printEntries: Entry[] = hasAccess("PRINT_CONTROL_PANEL")
    ? [{ key: "sales-print", path: "/sales-control-panel", label: t("sidebar.printControlPanel", "Print"), divider: true }]
    : [];

  const entriesBySection = { documents: [...documentEntries, ...printEntries], screens: screenEntries };
  const sections = SALES_NAV_SECTIONS.map(({ id, labelKey }) => ({
    key: id,
    label: t(labelKey),
    entries: entriesBySection[id],
  })).filter((section) => section.entries.length > 0);

  if (sections.length === 0) return null;

  const activeSection = sections.find((section) => section.key === (activeSectionKey || sections[0]?.key)) || sections[0];

  return (
    <div
      className="relative"
      onMouseEnter={() => openMenu("nonMaterial")}
      onMouseLeave={closeMenuDelayed}
    >
      <button
        type="button"
        onClick={() => (open ? closeMenu() : openMenu("nonMaterial"))}
        className="flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span>{t("sidebar.sales", "Продажба")}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && activeSection && (
        <div
          className="absolute left-0 top-full mt-1 flex rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xl"
          onMouseEnter={cancelClose}
        >
          <div className="w-56 p-2">
            {sections.map((section) => {
              const active = section.key === activeSection.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onMouseEnter={() => setActiveSectionKey(section.key)}
                  onClick={() => setActiveSectionKey(section.key)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors ${
                    active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                >
                  <span>{section.label}</span>
                  <ChevronRight size={15} />
                </button>
              );
            })}
          </div>

          <div className="max-h-[70vh] w-80 overflow-y-auto border-l border-gray-200 p-2">
            <div className="space-y-1">
              {activeSection.entries.map((entry) => {
                const active = location.pathname === entry.path || location.pathname.startsWith(`${entry.path}/`);
                return (
                  <React.Fragment key={`${activeSection.key}-${entry.key}`}>
                    {entry.divider && <div className="my-1 border-t border-gray-200" />}
                    <TopNavLink
                      to={entry.path}
                      state={entry.state}
                      label={entry.label}
                      moduleKey={entry.key}
                      isActive={active}
                      onClick={closeMenu}
                      className={`block truncate rounded px-2 py-1.5 text-xs transition-colors ${
                        active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                      }`}
                      style={active ? { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" } : undefined}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopNonMaterialMenu;
